import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Ellipsis } from 'lucide-react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { VFlex } from '@/components/base';
import Transition from '@/components/Common/Transition';
import Menu from '@/components/Menu/Menu';
import Typo, { Paragraph } from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { CHART_GRAPH } from '@/entities/chart/model/constants';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { DashboardPreprocess } from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/ChartPreprocessFactory';
import type { ChartAnalysisFeedbackListModalOptions } from '@/features/Analysis/types';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import {
  DASHBOARD_CARD_CHART_ALIAS_DATASET_KEY,
  Dashboard2ColumnType,
} from '@/features/Dashboard/types/constants';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import { Button, Tooltip } from '@/shared/ui';
import useDashboard2Store from '@/stores/Dashboard2Store';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import { track } from '@/utils/track/track';

import useDashboardCardDrag from '../../hooks/useDashboardCardDrag';
import ChartProvider from '../ChartProvider';
import type { DashboardCardProps } from '../DashboardCard';
import DashboardCardStyles from '../styles/DashboardCardStyles';
import DashboardCardContentHeader from './DashboardCardContentHeader';
import DashboardCardShell from './DashboardCardShell';

const { ChartWrapper } = DashboardCardStyles;

const DashboardChartContent = (props: DashboardCardProps) => {
  const color = useColor();

  const currDashboard = useDashboard2Store((state) => state.currDashboard);
  const globalFilter = useDashboard2Store((state) => state.globalFilter);
  const globalDateFilter = useDashboard2Store(
    (state) => state.globalDateFilter,
  );
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const {
    index,
    rowIndex,
    setSomeDragging,
    column,
    rowId,
    dashboardId,
    width,
  } = props;

  const { dragRef, previewRef, isDragging } = useDashboardCardDrag({
    index,
    rowIndex,
    setSomeDragging,
    column,
    rowId,
  });
  const chart = column.chart;
  if (!chart) throw new Error('chart is not exist');

  const wr = useWorkspaceRouter();

  const onClickDashboardCardTitle = () => {
    if (!column.chart) return;
    track(AMP_KEYS.DASHBOARD_CARD_CHART_TITLE_CLICK);
    wr.push(`/charts/${column.chart.alias}`);
  };

  const [historical, setHistorical] = useState(
    chart.historical?.value ?? false,
  );

  const chartParams = useMemo(() => {
    const preprocessFactory = new DashboardPreprocess({
      chart,
      historical,
      globalFilter,
      globalDateFilter,
    });
    const result = preprocessFactory.runPreprocess();
    return {
      ...result,
      name: chart.name,
    };
  }, [chart, historical, globalFilter, globalDateFilter]);

  const tableQuery = useQuery({
    queryKey: ['chart-table', JSON.stringify(chartParams)],
    queryFn: () => service.chart.fetchTable(chartParams),
    enabled: true,
  });

  const updateHistorical = async (historical: boolean) => {
    setHistorical(historical);
    const preprocessFactory = new DashboardPreprocess({
      chart,
      historical,
      globalFilter,
      globalDateFilter,
    });
    const params = preprocessFactory.runPreprocess();
    await service.chart.updateChart(chart.alias, params);
  };

  const queryClient = useQueryClient();
  const setPopup = usePopup();
  const addToast = useToast();

  const onClickRemoveChartFromDashboard = async () => {
    track(AMP_KEYS.DASHBOARD_CARD_REMOVE_DASHBOARD_CLICK);
    const hasAnalysis = currDashboard?.analyses?.find(
      (analysis) => analysis.reference_id === chart.alias,
    );
    if (hasAnalysis) {
      setPopup({
        popupTheme: 'negative',
        type: 'confirm',
        title: 'Remove from dashboard',
        message: (
          <Paragraph type="body2">
            This chart is connected to one or more Smart Briefs. Removing the
            chart will also{' '}
            <span style={{ color: color.red600 }}>
              delete all linked Smart Briefs.
            </span>
            <br />
            <br />
            Are you sure you want to remove this chart?
          </Paragraph>
        ),
        confirmText: 'Remove',
        cancelText: 'Cancel',
        onConfirm: async () => {
          try {
            await service.dashboard2.deleteColumnInDashboardRow(
              dashboardId,
              rowId,
              column.id,
            );

            // refetch dashboard
            queryClient.invalidateQueries({
              queryKey: ['dashboard2', dashboardId],
            });

            addToast('Chart removed from dashboard', 'success');
          } catch (e) {
            addToast('Error! try again', 'fail');
          }
        },
      });
      return;
    }

    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Remove from dashboard',
      message: 'Are you sure you want to remove this chart from the dashboard?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await service.dashboard2.deleteColumnInDashboardRow(
            dashboardId,
            rowId,
            column.id,
          );

          // refetch dashboard
          queryClient.invalidateQueries({
            queryKey: ['dashboard2', dashboardId],
          });

          addToast('Chart removed from dashboard', 'success');
        } catch (e) {
          addToast('Error! try again', 'fail');
        }
      },
    });
  };

  const onClickDuplicateChart = async () => {
    const newChart = await service.chart.duplicateChart(chart.alias);
    await service.dashboard2.addDashboardRow(dashboardId, {
      pos: rowIndex,
      columns: [
        {
          type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
          chart: newChart.alias,
        },
      ],
    });

    queryClient.invalidateQueries({ queryKey: ['dashboard2', dashboardId] });
    addToast('Chart duplicated', 'success');
  };

  const onClickAskSyncly = () => {
    track(AMP_KEYS.DASHBOARD_CARD_ASK_SYNCLY_CLICK);

    if (!tableQuery.data) return;
    const { itemX } = chartUtils.getChartFeedbackListParams(
      chart,
      tableQuery.data,
    );

    setNewModal<ChartAnalysisFeedbackListModalOptions>({
      alias: chart.alias,
      type: DETAIL_MODAL_TYPE.CHART_ANALYSIS,
      options: {
        chart,
        chart_alias: chart.alias,
        dashboard_alias: dashboardId,
        isAll: true,
        displayed_x_items: itemX || [],
        dashboardCardInfo: {
          rowIndex,
          dashbaordAlias: dashboardId,
        },
        initialState: CollapseState.EXPANDED,
      },
    });
  };

  return (
    <DashboardCardShell
      previewRef={previewRef}
      dragRef={dragRef}
      isDragging={isDragging}
      width={width}
      wrapperStyle={{ height: '100%' }}
      cardAttributes={{
        [DASHBOARD_CARD_CHART_ALIAS_DATASET_KEY]: chart.alias,
      }}
    >
      <Transition
        status={true}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Style>
          <DashboardCardContentHeader
            title={
              <Typo type="body0" weight="m" multilineEllipsis={2}>
                {column.chart?.name}
              </Typo>
            }
            onTitleClick={onClickDashboardCardTitle}
            rightAction={
              <>
                {chartParams.graph !== CHART_GRAPH.WORD_CLOUD && (
                  <Tooltip content="Create smart brief">
                    <span>
                      <Button
                        size="icon-sm"
                        onClick={onClickAskSyncly}
                        variant="ghost"
                      >
                        <AIIcon className="size-5" />
                      </Button>
                    </span>
                  </Tooltip>
                )}

                <Menu
                  triggerNode={
                    <span>
                      <Button
                        size="icon-sm"
                        onClick={() => {
                          track(AMP_KEYS.DASHBOARD_CARD_ELLIPSIS_CLICK);
                        }}
                        variant="ghost"
                      >
                        <Ellipsis className="size-4.5" />
                      </Button>
                    </span>
                  }
                  modalWidth="200px"
                  options={[
                    {
                      label: 'Duplicate',
                      onClick: onClickDuplicateChart,
                    },
                    {
                      label: 'Remove from dashboard',
                      onClick: onClickRemoveChartFromDashboard,
                    },
                  ]}
                />
              </>
            }
          />
          <ChartWrapper>
            <ChartProvider
              chartAlias={chart.alias}
              chart={chartParams}
              tableQuery={tableQuery}
              onHistoricalChange={updateHistorical}
              dashboardCardInfo={{
                rowIndex,
                dashbaordAlias: dashboardId,
              }}
            />
          </ChartWrapper>
        </Style>
      </Transition>
    </DashboardCardShell>
  );
};

const Style = styled(VFlex)`
  height: 100%;
`;

export default DashboardChartContent;
